import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import {
  LIST_PAGE_CONFIG,
  ListPageConfig,
  ListColumnConfig,
} from 'app/main/components/list-page-config/list-page.config';

import { ListCardItem } from 'app/main/components/list-card/list-card.component';

import { RolesService } from '../../../../core/services/user/roles.service';
import { ToastService } from '../../../../core/services/toast.service';
import { PermissionService } from '../../../../core/services/user/permission.service';
import { CurrentUserService } from '../../../../core/services/auth/current-user.service';
import { OrganizationService } from '../../../../core/services/organization/organization.service';
import { Organization } from '../../../../core/models/organization/organization.model';
import { DynamicListConfig } from 'app/core/models/table/dynamic-list.model';
import { DynamicListComponent } from 'app/shared/components/dynamic-list/dynamic-list.component';
import { SelectComponent } from 'app/shared/components/select/select.component';
import { PageSizeSelectorComponent } from 'app/shared/components/page-size-selector/page-size-selector.component';
import { HasClaimDirective } from 'app/shared/directives/has-claim.directive';
import { BaseListComponent } from 'app/shared/base/base-list.component';
import { ListStateStackService } from 'app/core/services/list/list-state-stack.service';

export interface RoleItem {
  role: Role;
}

export interface Role {
  id: string;
  name: string;
  roleClaims?: any[];
  roleOrganizations?: {
    organizationId: string;
    organizationName: string;
  }[];
}

@Component({
  selector: 'app-role-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    DynamicListComponent,
    SelectComponent,
    PageSizeSelectorComponent,
    HasClaimDirective,
  ],
  templateUrl: './role-list.component.html',
  styleUrl: './role-list.component.scss',
})
export class RoleListComponent extends BaseListComponent implements OnInit, OnDestroy, AfterViewInit {
  roleItems: RoleItem[] = [];
  roleCards: ListCardItem[] = [];

  totalItems = 0;
  totalPage = 0;
  pageSizes = [10, 15, 25, 50, 100];
  pageHeight!: number;
  selectedRoleId: string | null = null;

  @ViewChild('openDeleteModalBtn')
  openDeleteModalBtn!: ElementRef<HTMLButtonElement>;

  isLoading = true;

  organizations: Organization[] = [];
  isSystemAdmin = false;

  override filterForm: {
    name: string;
    organizationId: string | null;
  } = {
    name: '',
    organizationId: null,
  };

  constructor(
    private rolesService: RolesService,
    private translate: TranslateService,
    private toastService: ToastService,
    public permissionService: PermissionService,
    private currentUserService: CurrentUserService,
    private organizationService: OrganizationService,
    listStateStack: ListStateStackService,
  ) {
    super(listStateStack, 'role-list');
    this.collapseElementId = 'collapseRole'; // Set the collapse element ID for this list page
    const userLang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(userLang);
  }

  roleConfig: DynamicListConfig = {
    pageTitleKey: 'roleList.title',
    columns: [
      {
        key: 'name',
        labelKey: 'roleList.roleName',
        type: 'text',
        width: 'minmax(280px, 2.2fr)',
      },

      {
        key: 'roleClaims',
        labelKey: 'roleList.claimsCount',
        type: 'text',
        width: 'minmax(120px, 1fr)',
        transform: (val: any[]) => val?.length?.toString() || '0',
      },
      {
        key: 'roleOrganizations',
        labelKey: 'ORGANIZATION',
        type: 'text',
        width: 'minmax(150px, 1.3fr)',
        transform: (val: any[]) =>
          val?.map((o) => o.organizationName).join(', ') || '-',
      },
      {
        key: 'actions',
        labelKey: 'ACTIONS',
        type: 'actions',
        width: 'minmax(140px, 0.8fr)',
      },
    ],
    actions: [
      {
        icon: 'fa-regular fa-pen-to-square',
        title: 'edit',
        permission: 'update_role_claim',
        showIf: () =>
          this.permissionService.hasPermission('get_role_by_id_claim'),
        onClick: (item) => this.onEdit(item),
      },
      {
        icon: 'fa-regular fa-trash-can',
        title: 'delete',
        permission: 'delete_role_claim',
        onClick: (item) => this.onDeleteCard(item),
      },
    ],
  };

  activeTooltip: { text: string; x: number; y: number } | null = null;
  private tooltipTimeout: any = null;
  private pendingTooltipPosition: { x: number; y: number } | null = null;

  showTooltip(event: MouseEvent, text: string) {
    if (!text) return;
    const translatedText = this.translate.instant(text);

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    const x = rect.left;
    const y = rect.bottom + 4;

    this.pendingTooltipPosition = { x, y };

    if (this.tooltipTimeout) clearTimeout(this.tooltipTimeout);
    this.tooltipTimeout = setTimeout(() => {
      if (this.pendingTooltipPosition) {
        this.activeTooltip = {
          text: translatedText,
          x: this.pendingTooltipPosition.x,
          y: this.pendingTooltipPosition.y,
        };
      }
    }, 300);
  }

  hideTooltip() {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }
    this.pendingTooltipPosition = null;
    this.activeTooltip = null;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.checkUserRoleAndLoadOrganizations();
    if (!this.stateWasRestored) {
      this.getRoleList();
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    this.restoreCollapseState();
    this.setupCollapseListener();
  }

  private setupCollapseListener(): void {
    if (!this.collapseElementId) return;
    const collapseElement = document.getElementById(this.collapseElementId);
    if (collapseElement) {
      collapseElement.addEventListener('show.bs.collapse', () => this.updateCollapseState(true));
      collapseElement.addEventListener('hide.bs.collapse', () => this.updateCollapseState(false));
    }
  }

  override loadData(): void {
    this.getRoleList();
  }

  private checkUserRoleAndLoadOrganizations(): void {
    const currentUser = this.currentUserService.GetSession();
    this.isSystemAdmin = currentUser?.role?.name === 'System Admin';

    if (this.isSystemAdmin) {
      // System Admin sees all organizations
      this.organizationService.getAllOrganizationItems().subscribe({
        next: (orgs) => {
          this.organizations = orgs || [];
        },
        error: (err) => {
          console.error('Error loading organizations:', err);
          this.organizations = [];
        },
      });
    } else {
      // Other users see only their own organizations
      const userOrganizations = currentUser?.userOrganizations || [];
      this.organizations = userOrganizations.map((org: any) => ({
        id: org.organizationId,
        name: org.organizationName,
      }));
    }
  }



  getRoleList(): void {
    this.isLoading = true;
    const pageIndex = this.currentPage - 1; // API uses 0-based index
    const organizationId = this.filterForm.organizationId || null;
    const name = this.filterForm.name || null;
    this.rolesService
      .getRoles(pageIndex, this.pageSize, organizationId, name)
      .subscribe({
        next: (res) => {
          const data = res.data;

          if (data && data.items) {
            this.roleItems = data.items;
            this.roleCards = this.roleItems.map((item) =>
              this.mapToListCardItem(item),
            );

            this.totalItems = data.count || this.roleItems.length;
            this.totalPage = data.pages || 1;
          } else if (Array.isArray(data)) {
            this.roleItems = data;
            this.roleCards = this.roleItems.map((item) =>
              this.mapToListCardItem(item),
            );
            this.totalItems = this.roleItems.length;
            this.totalPage = 1;
          }

          // Backend already handles all filtering (including name), no need to re-filter
          this.isLoading = false;
        },
        error: (err) => {
          console.error('getRoles error', err);
          this.isLoading = false;
        },
      });
  }

  private mapToListCardItem(item: RoleItem): ListCardItem {
    const role = item.role;
    return {
      id: role.id,
      name: role.name,
      organization:
        role.roleOrganizations?.map((o) => o.organizationName).join(', ') ||
        '-',
      role: role.roleClaims?.length?.toString() || '0',
      email: '',
      lastLoginDate: '',
    };
  }

  applyNameFilter(): void {
    let items = this.roleItems;

    // Only apply name filter on frontend (organization is filtered by backend)
    if (this.filterForm.name.trim()) {
      const filterName = this.filterForm.name.toLowerCase();
      items = items.filter((item) =>
        item.role.name.toLowerCase().includes(filterName),
      );
    }

    this.roleCards = items.map((item) => this.mapToListCardItem(item));
  }

  filterList(e: Event): void {
    e.preventDefault();
    this.currentPage = 1; // Reset to first page when filtering
    this.getRoleList(); // Call backend with new filters
  }

  clearFilters(): void {
    this.filterForm = { name: '', organizationId: null };
    this.currentPage = 1;
    this.getRoleList(); // Reload from backend without filters
  }

  changePage(page: number): void {
    this.currentPage = page + 1;
    this.getRoleList();
  }

  previousPage(page: number): void {
    if (this.currentPage > 1) {
      this.currentPage = this.currentPage - 1;
      this.getRoleList();
    }
  }

  nextPage(page: number): void {
    if (this.currentPage < this.totalPage) {
      this.currentPage = this.currentPage + 1;
      this.getRoleList();
    }
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getRoleList();
  }

  onCardClick(card: ListCardItem): void {
    if (
      this.permissionService.hasPermission('update_role_claim') &&
      this.permissionService.hasPermission('get_role_by_id_claim') &&
      this.permissionService.hasPermission('get_list_claim')
    ) {
      this.router.navigate(['/role/edit', card.id]);
    }
  }

  onEdit(card: ListCardItem): void {
    this.router.navigate(['/role/edit', card.id]);
  }

  navigateToCreate(): void {
    this.router.navigate(['/role/create']);
  }

  onDeleteCard(card: ListCardItem): void {
    this.selectedRoleId = String(card.id);
    this.openDeleteModalBtn.nativeElement.click();
  }

  confirmDelete(): void {
    if (!this.selectedRoleId) return;

    this.rolesService.deleteRole(this.selectedRoleId).subscribe({
      next: (res) => {
        this.toastService.showToast(
          this.translate.instant('roleList.roleDeletedSuccess'),
          'success',
        );
        this.getRoleList();
        this.selectedRoleId = null;
      },
    });
  }

  getColClass(col: ListColumnConfig): string {
    switch (col.colType) {
      case 'user':
        return 'col-user';
      case 'name':
        return 'col-name';
      case 'dataOrg':
        return 'col-data-org';
      case 'dataEmail':
        return 'col-data-email';
      case 'actions':
        return 'col-actions';
      default:
        return 'col-data';
    }
  }
}
